/**
 * Tooltip — portaled via Portal so it can never paint over the guide drawer.
 *
 * B-2026-0085: Tooltip was previously rendered as an `absolute z-50` child
 * inside #root. This caused it to visually overlap the guide overlay's right
 * edge region. Fix: render tooltip content into Portal at z-[9500] (below the
 * drawer at z-[10000] but above all normal page stacking contexts).
 *
 * The anchor ref tracks the trigger element's position so the Portal-ed tooltip
 * can be absolutely positioned in viewport space. Recalculates on mouse-enter
 * via a ResizeObserver-less approach (position is stable while the tooltip is
 * visible; re-opening recalculates).
 */

import { useState, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

interface TooltipProps {
  content: string | ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

interface TooltipPosition {
  top: number;
  left: number;
}

function Tooltip({ content, children, position = 'top', className }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<TooltipPosition>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const OFFSET = 8; // px gap between trigger and tooltip

  const computeCoords = useCallback((rect: DOMRect): TooltipPosition => {
    switch (position) {
      case 'top':
        return {
          top: rect.top - OFFSET,
          left: rect.left + rect.width / 2,
        };
      case 'bottom':
        return {
          top: rect.bottom + OFFSET,
          left: rect.left + rect.width / 2,
        };
      case 'left':
        return {
          top: rect.top + rect.height / 2,
          left: rect.left - OFFSET,
        };
      case 'right':
        return {
          top: rect.top + rect.height / 2,
          left: rect.right + OFFSET,
        };
    }
  }, [position]);

  const handleMouseEnter = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords(computeCoords(rect));
    }
    setIsVisible(true);
  }, [computeCoords]);

  const handleMouseLeave = useCallback(() => setIsVisible(false), []);

  // CSS transform to correctly anchor the tooltip relative to the computed point.
  const transformByPosition: Record<string, string> = {
    top: 'translate(-50%, -100%)',
    bottom: 'translate(-50%, 0)',
    left: 'translate(-100%, -50%)',
    right: 'translate(0, -50%)',
  };

  // Portal target: prefer #portal-root, fall back to document.body.
  const portalTarget =
    (typeof document !== 'undefined'
      ? document.getElementById('portal-root') ?? document.body
      : null);

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {children}
      </div>
      {isVisible && portalTarget && createPortal(
        <div
          className={cn(
            'fixed px-2 py-1 text-xs font-medium text-text-primary bg-dark-elevated-1 border border-border-subtle rounded shadow-modal pointer-events-none',
            typeof content === 'string' && 'whitespace-nowrap',
            className
          )}
          style={{
            top: coords.top,
            left: coords.left,
            transform: transformByPosition[position],
            zIndex: 9500,
          }}
          role="tooltip"
        >
          {content}
          <div
            className={cn('absolute w-2 h-2 bg-dark-elevated-1 border border-border-subtle transform rotate-45', {
              'top-full left-1/2 -translate-x-1/2 -mt-1 border-t-0 border-l-0': position === 'top',
              'bottom-full left-1/2 -translate-x-1/2 -mb-1 border-b-0 border-r-0': position === 'bottom',
              'top-1/2 left-full -translate-y-1/2 -ml-1 border-l-0 border-b-0': position === 'left',
              'top-1/2 right-full -translate-y-1/2 -mr-1 border-r-0 border-t-0': position === 'right',
            })}
          />
        </div>,
        portalTarget
      )}
    </div>
  );
}

export default Tooltip;
